import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';
import { AppService } from '../../app.service';
import { DocumentType } from '../../models/documentType';
import { DocumentVisibility } from '../../models/documentVisibility';
import { VaultDocument } from '../../models/vaultDocument';
import { VisibleTo } from '../../models/visibleTo';

@Component({ standalone: false,
  selector: 'document-upload-dialog',
  templateUrl: 'document-upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['document-upload-dialog.component.scss']
})

//THIS IS FOR VAULTDOCUMENTS ONLY
export class DocumentUploadDialogComponent {
  title: string= "";
  confirmButtonText = "Upload";
  cancelButtonText = "Cancel";

  public busy:boolean=false;
  public uploadForm:FormGroup;
  
  public context?:string;
  public contextId?:number;
  public restrictedToUserId?:string;
  public contextDescription?:string;
  public vaultDocument?:VaultDocument;
  
  public selectedFile?:File;

  private _documentTypeArray:DocumentType[]=[];
  private visibilitySubscription?:Subscription;
  
  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<DocumentUploadDialogComponent>, private appService:AppService) {
    this.uploadForm = new FormGroup({
      filename : new FormControl<string | undefined>(undefined, Validators.compose([Validators.required, Validators.minLength(5)])),
      description : new FormControl<string | undefined>(undefined),
      documentType : new FormControl<DocumentType | undefined>(undefined, Validators.required),
      visibility : new FormControl<DocumentVisibility | undefined>(undefined, Validators.required),
      visibleToClient : new FormControl<boolean>(false, { nonNullable: true })
    });
    //public documents are visible to everyone: role options don't apply (disabled, value kept for switching back)
    this.visibilitySubscription = this.uploadForm.get('visibility')!.valueChanges.subscribe(() => this.updateRoleOptions());
    if(data){
      this.title=data.title;
      this.context=data.context;
      this.contextId=data.contextId;
      this.contextDescription=data.contextDescription?data.contextDescription:"New Property";
      this.restrictedToUserId=data.restrictedToUserId;
      this.vaultDocument=data.vaultDocument;
      if(data.confirmButtonText) this.confirmButtonText=data.confirmButtonText;
    }
    this.createForm();
    this.appService.documentTypes.pipe(take(1)).subscribe(dtArray=>{
      this._documentTypeArray=dtArray.filter(dt => dt.buyerUploadOnly==false); //filter out document types that are only for buyer uploads
      if(this.vaultDocument) this.populateForm();
    });
  }

  private createForm(){
    if(this.restrictedToUserId){
      this.uploadForm.controls['visibility'].setValue(this.documentVisibilityList.find(v=>v.id==0)); //user restricted docs: not public (access via restrictedToUserId), no selection of visibility
    }
    if(this.context=="inventory") {
      this.uploadForm.controls['visibility'].setValue(this.documentVisibilityList.find(v=>v.id==0)); //for inventory all docs are internal
    }
  }

  ngOnDestroy(){
    this.visibilitySubscription?.unsubscribe();
  }

  /** public documents (9) are visible to everyone */
  get isPublic(){
    return this.uploadForm.get('visibility')!.value?.id==9;
  }

  private updateRoleOptions(){
    const roleControl = this.uploadForm.get('visibleToClient')!;
    if(this.isPublic) roleControl.disable({ emitEvent:false });
    else roleControl.enable({ emitEvent:false });
  }

  /** roles that see the document (not for user restricted or inventory documents) */
  /** roles that see the document; without role selection (user or inventory documents) the stored value stays */
  private get selectedVisibleTo(){
    if(!this.showRoleSelection) return this.vaultDocument?.visibleTo ?? VisibleTo.None;
    return VisibleTo.set(this.vaultDocument?.visibleTo, VisibleTo.Client, this.uploadForm.get('visibleToClient')!.value);
  }

  /** document restricted to one user (new upload for a user, or an existing restricted document, e.g. an executed NDA) */
  get isRestricted(){
    return !!this.restrictedToUserId || !!this.vaultDocument?.restrictedToUserId;
  }

  /**
   * documents of a property or auction can be released to client users of the property's client, restricted ones too
   * (no release for user documents without property or for inventory)
   */
  get showRoleSelection(){
    return this.context=='property' || this.context=='auction';
  }

  get documentTypeArray(){
    return this._documentTypeArray.filter(dt=>dt.id!=2); //exclude type 2 which is legal ad as legal ads cannot be handled here
  }

  get documentVisibilityList(){
    return this.appService.documentVisibilityList;
  } 

  get canUpload(){
    return (this.selectedFile!=undefined || this.editMode) && this.uploadForm.valid && this.uploadForm.dirty;
  }

  get fileSelected(){
    return this.selectedFile!=undefined || this.editMode;
  }

  /** public scope only for documents that aren't restricted to a user (restricted documents are never public) */
  get visibilitySelectionRequired(){
    return !this.isRestricted && this.context!="inventory";
  }

  get editMode(){
    return this.vaultDocument!=undefined;
  }

  private populateForm(){
    if(this.vaultDocument){
      this.uploadForm.get("filename")!.setValue(this.vaultDocument.filename);
      //the file name of an existing document can't be changed (uspStoreVaultDocument doesn't update it)
      this.uploadForm.get("filename")!.disable();
      this.uploadForm.get("description")!.setValue(this.vaultDocument.description);
      this.uploadForm.get("documentType")!.setValue(this._documentTypeArray.find(t=>t.id==this.vaultDocument!.documentTypeId));
      this.uploadForm.get("visibility")!.setValue(this.documentVisibilityList.find(v=>v.id==this.vaultDocument!.visibility))
      this.uploadForm.get("visibleToClient")!.setValue(VisibleTo.includes(this.vaultDocument.visibleTo, VisibleTo.Client));
      this.updateRoleOptions();
    }
  }

  onFileChange($event:any) {
    this.selectedFile = $event.target.files[0];
    this.uploadForm.controls['filename'].setValue(this.selectedFile ? this.selectedFile.name : '');
}

  onConfirmClick(): void {
    if(!this.editMode){
      let vd=new VaultDocument();
      vd.id=0;
      vd.file=this.selectedFile;
      vd.filename= this.uploadForm.get("filename")!.value;
      vd.description=this.uploadForm.get("description")!.value;
      vd.documentTypeId=this.uploadForm.get("documentType")!.value.id;
      vd.visibility = this.uploadForm.get("visibility")!.value.id;
      vd.visibleTo = this.selectedVisibleTo;
      vd.context=this.context;
      vd.contextId=this.contextId;
      vd.restrictedToUserId=this.restrictedToUserId;
      vd.contentType = this.selectedFile?.type;
      this.dialogRef.close(vd);
    }
    else if(this.vaultDocument){
      if(this.vaultDocument.id ?? 0 >0) this.vaultDocument.updated=true; //only set this to true if document has an id which means was saved before and is not an edit of a new unsaved document
      this.vaultDocument.filename= this.uploadForm.get("filename")!.value;
      this.vaultDocument.description=this.uploadForm.get("description")!.value;
      this.vaultDocument.documentTypeId=this.uploadForm.get("documentType")!.value.id;
      this.vaultDocument.visibility = this.uploadForm.get("visibility")!.value.id;
      this.vaultDocument.visibleTo = this.selectedVisibleTo;
      this.dialogRef.close(this.vaultDocument);
    }
    else this.dialogRef.close(false);
  }

  onCancelClick(): void {
    this.dialogRef.close(false);
  }

}